import { fieldConfig } from "@autoform/zod";
import { z } from "zod";

import { ENRICHMENT_STATUSES } from "@/lib/enrichment";

const disabled = fieldConfig({ inputProps: { disabled: true } });

const refMedia = z
  .object({
    photos: z
      .array(z.object({ url: z.string() }).passthrough())
      .nullish()
      .default([]),
  })
  .passthrough()
  .nullish();

/** The denormalized `ScreeningMovieDbDto` copy — display only; `movieId` is what is sent. */
export const screeningMovieRefSchema = z
  .object({
    id: z.string(),
    title: z.string().nullish(),
    media: refMedia,
    durationInMinutes: z.number().nullish(),
  })
  .passthrough();

/** The denormalized `ScreeningVenueDbDto` copy — display only; `venueId` is what is sent. */
export const screeningVenueRefSchema = z
  .object({
    id: z.string(),
    name: z.string().nullish(),
    address: z.string().nullish(),
    timezone: z.string().nullish(),
    media: refMedia,
  })
  .passthrough();

export type ScreeningMovieRef = z.infer<typeof screeningMovieRefSchema>;
export type ScreeningVenueRef = z.infer<typeof screeningVenueRefSchema>;

// FluentValidation `MustBeIso6391` on `Format.Language` and `Format.Subtitles`.
const iso6391 = z
  .string()
  .regex(/^[a-z]{2}$/, "Must be a 2-letter ISO 639-1 code, e.g. el");

/**
 * Edit schema for a Screening, bound to `ScreeningDbDto` from `GET /api/screenings/{id}` and
 * mapped onto `UpdateScreeningCommand` by `useScreeningRepository.update`.
 *
 * `movie` and `venue` are re-pointed by id (`movieId`/`venueId`), and the backend re-copies
 * them; `endDate` is derived from the start and the movie's duration, so it is read-only.
 * `startDate` holds the picker's browser-local wall clock and is restamped through the venue
 * timezone by `performance-start-date.ts`, both ways — see `ScreeningsEdit`.
 *
 * The rules mirror `UpdateScreeningCommandValidator`, so a bad value is caught here and not as a
 * 400 for the whole request.
 */
export const screeningEditSchema = z.object({
  id: z.string().nullish().superRefine(disabled),
  movie: screeningMovieRefSchema.superRefine(
    fieldConfig({ label: "Movie", fieldType: "screeningMovie" }),
  ),
  venue: screeningVenueRefSchema.superRefine(
    fieldConfig({ label: "Venue", fieldType: "screeningVenue" }),
  ),
  startDate: z
    .string()
    .datetime({ offset: true })
    .superRefine(fieldConfig({ fieldType: "datetime", label: "Start date" })),
  endDate: z
    .string()
    .datetime({ offset: true })
    .nullish()
    .superRefine(
      fieldConfig({
        fieldType: "datetime",
        label: "End date (from the movie's duration)",
        inputProps: { disabled: true },
      }),
    ),
  format: z.object({
    is3D: z
      .boolean()
      .default(false)
      .superRefine(fieldConfig({ label: "3D" })),
    language: iso6391,
    subtitles: iso6391.nullish(),
  }),
  screen: z.object({
    name: z.string().min(1, "Required"),
    type: z.enum(["Indoor", "OpenAir"]),
  }),
  pricing: z
    .array(
      z.object({
        label: z.string().min(1, "Required"),
        amount: z.coerce.number().min(0),
        currency: z
          .string()
          .regex(/^[A-Z]{3}$/, "Must be a 3-letter ISO 4217 code")
          .default("EUR"),
      }),
    )
    .nullish()
    .default([]),
  ticketUrl: z
    .string()
    .url("Must be an http(s) link")
    .nullish()
    .superRefine(fieldConfig({ label: "Ticket URL" })),
  status: z.enum(["Draft", "Complete", "Cancelled"]),
  tags: z
    .array(z.string())
    .nullish()
    .default([])
    .superRefine(fieldConfig({ fieldType: "tagList" })),
  hide: z.boolean().default(false),
  enrichmentStatus: z
    .enum(ENRICHMENT_STATUSES)
    .nullish()
    .superRefine(fieldConfig({ fieldType: "enrichmentStatus" })),
  createdBy: z.string().nullish().superRefine(disabled),
  createdAt: z
    .string()
    .datetime({ offset: true })
    .nullish()
    .superRefine(
      fieldConfig({ fieldType: "datetime", inputProps: { disabled: true } }),
    ),
  updatedBy: z.string().nullish().superRefine(disabled),
  updatedAt: z
    .string()
    .datetime({ offset: true })
    .nullish()
    .superRefine(
      fieldConfig({ fieldType: "datetime", inputProps: { disabled: true } }),
    ),
});

export type ScreeningEditFormType = z.infer<typeof screeningEditSchema>;
